import { Form } from 'antd';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo } from 'react';

import {
  type ModelJournalEmployee,
  useUpdateWorkScheduleMutation,
} from '@app/api/WorkSchedule';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { generateTimes } from '@app/utils';

import { useCalendarContext } from '@pages/Calendar/context';

import type { Dayjs } from 'dayjs';
import type { Moment } from 'moment';

const EMPTY_EMPLOYEES: ModelJournalEmployee[] = [];
const EMPTY_ACTIVE_WORKDAY_EMPLOYEE_IDS: string[] = [];

const timeOptions = generateTimes().map((label, index) => ({
  value: index,
  label,
}));

interface AddWorkScheduleFormValues {
  employeeId?: number;
  date: Dayjs;
  timeS: number;
  timeE: number;
}

interface UseAddWorkScheduleModalParams {
  employeeId?: number;
  employeeName?: string;
  employees?: ModelJournalEmployee[];
  isOpen: boolean;
  selectedDateMoment?: Moment;
  refreshCalendarData?: () => void;
  onClose(): void;
}

export const useAddWorkScheduleModal = ({
  employeeId,
  employeeName,
  employees,
  isOpen,
  selectedDateMoment: externalSelectedDateMoment,
  refreshCalendarData: externalRefreshCalendarData,
  onClose,
}: UseAddWorkScheduleModalParams) => {
  const [form] = Form.useForm<AddWorkScheduleFormValues>();
  const { params } = useUserPrint();
  const { showNotification } = useNotification();

  const {
    branchStartTime,
    branchEndTime,
    selectedDateMoment: contextSelectedDateMoment,
    refreshCalendarData: contextRefreshCalendarData,
    addDisplayedEmployeeId,
    activeWorkdayEmployeeIds = EMPTY_ACTIVE_WORKDAY_EMPLOYEE_IDS,
    addActiveWorkdayEmployeeId,
  } = useCalendarContext();

  const employeeList = employees ?? EMPTY_EMPLOYEES;

  const selectedDateMoment =
    externalSelectedDateMoment || contextSelectedDateMoment;

  const refreshCalendarData = useCallback(() => {
    if (externalRefreshCalendarData) {
      externalRefreshCalendarData();
      return;
    }

    contextRefreshCalendarData();
  }, [contextRefreshCalendarData, externalRefreshCalendarData]);

  const [updateWorkSchedule, { isLoading }] = useUpdateWorkScheduleMutation();

  const watchedEmployeeId = Form.useWatch('employeeId', form) as
    | number
    | undefined;

  const isEmployeeSelectable = employeeId == null;
  const selectedEmployeeId = employeeId ?? watchedEmployeeId;

  const availableEmployeesForScheduleModal = useMemo(() => {
    if (!isEmployeeSelectable || activeWorkdayEmployeeIds.length === 0) {
      return employeeList;
    }

    const activeWorkdayEmployeeIdsSet = new Set(activeWorkdayEmployeeIds);

    return employeeList.filter(
      ({ Id }) => !activeWorkdayEmployeeIdsSet.has(String(Id))
    );
  }, [activeWorkdayEmployeeIds, employeeList, isEmployeeSelectable]);

  const getEmployeeDisplayName = useCallback(
    (employee?: ModelJournalEmployee) => {
      if (!employee) {
        return '';
      }

      return (
        employee.NameFull ||
        `${employee.NameLast || ''} ${employee.NameFirst || ''}`.trim() ||
        `ID ${employee.Id}`
      );
    },
    []
  );

  const selectedEmployee = useMemo(
    () =>
      availableEmployeesForScheduleModal.find(
        ({ Id }) => Id === selectedEmployeeId
      ),
    [availableEmployeesForScheduleModal, selectedEmployeeId]
  );

  const selectedEmployeeName = useMemo(
    () => employeeName || getEmployeeDisplayName(selectedEmployee),
    [employeeName, getEmployeeDisplayName, selectedEmployee]
  );

  const employeeOptions = useMemo(
    () =>
      availableEmployeesForScheduleModal.map((employee) => ({
        value: employee.Id,
        label: getEmployeeDisplayName(employee),
      })),
    [availableEmployeesForScheduleModal, getEmployeeDisplayName]
  );

  const extractTimeLabel = useCallback((value?: string | null) => {
    if (!value) {
      return undefined;
    }

    const isoTimeMatch = value.match(/T(\d{2}:\d{2})/);

    if (isoTimeMatch?.[1]) {
      return isoTimeMatch[1];
    }

    return value.slice(0, 5);
  }, []);

  const resolveTimeValue = useCallback((timeLabel?: string | null) => {
    if (!timeLabel) {
      return undefined;
    }

    return timeOptions.find((option) => option.label === timeLabel)?.value;
  }, []);

  const branchTimeRange = useMemo(() => {
    const branchStartTimeValue = resolveTimeValue(
      extractTimeLabel(branchStartTime)
    );

    const branchEndTimeValue = resolveTimeValue(
      extractTimeLabel(branchEndTime)
    );

    return {
      timeS: branchStartTimeValue ?? 0,
      timeE: branchEndTimeValue ?? timeOptions.length - 1,
    };
  }, [branchEndTime, branchStartTime, extractTimeLabel, resolveTimeValue]);

  const applyBranchTimeRange = useCallback(() => {
    form.setFieldsValue({
      timeS: branchTimeRange.timeS,
      timeE: branchTimeRange.timeE,
    });
  }, [branchTimeRange.timeE, branchTimeRange.timeS, form]);

  const defaultDate = useMemo(
    () =>
      dayjs(
        selectedDateMoment?.format('YYYY-MM-DD') || dayjs().format('YYYY-MM-DD')
      ),
    [selectedDateMoment]
  );

  const handleDateChange = useCallback(() => {
    applyBranchTimeRange();
  }, [applyBranchTimeRange]);

  const handleEmployeeChange = useCallback(() => {
    applyBranchTimeRange();
  }, [applyBranchTimeRange]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    form.setFieldsValue({
      employeeId: isEmployeeSelectable ? undefined : employeeId,
      date: defaultDate,
      timeS: branchTimeRange.timeS,
      timeE: branchTimeRange.timeE,
    });
  }, [
    branchTimeRange.timeE,
    branchTimeRange.timeS,
    defaultDate,
    employeeId,
    form,
    isEmployeeSelectable,
    isOpen,
  ]);

  const buildIsoString = (dateString: string, timeLabel: string): string => {
    const [hours, minutes] = timeLabel.split(':');

    return `${dateString}T${hours}:${minutes}:00.000Z`;
  };

  const handleFinish = useCallback(
    async (values: AddWorkScheduleFormValues) => {
      const targetEmployeeId = employeeId ?? values.employeeId;

      if (!targetEmployeeId) {
        return;
      }

      if (!params.ActiveBranchId || !params.UserId) {
        return;
      }

      const dateString = values.date.format('YYYY-MM-DD');

      const timeSLabel = timeOptions.find(
        (option) => option.value === values.timeS
      )?.label;

      const timeELabel = timeOptions.find(
        (option) => option.value === values.timeE
      )?.label;

      if (!timeSLabel || !timeELabel) {
        return;
      }

      const TimeS = buildIsoString(dateString, timeSLabel);
      const TimeE = buildIsoString(dateString, timeELabel);

      try {
        await updateWorkSchedule({
          ActiveBranchId: params.ActiveBranchId,
          data: {
            Id: 0,
            UserId: targetEmployeeId,
            BranchId: params.ActiveBranchId,
            TimeS,
            TimeE,
            IsIgnoreRecords: !isEmployeeSelectable,
          },
        }).unwrap();

        showNotification(
          isEmployeeSelectable
            ? 'Исполнитель добавлен в расписание'
            : 'Рабочий день в расписание добавлен',
          'success'
        );

        if (
          selectedDateMoment &&
          dateString === selectedDateMoment.format('YYYY-MM-DD')
        ) {
          addDisplayedEmployeeId(String(targetEmployeeId));
          addActiveWorkdayEmployeeId(String(targetEmployeeId));
        }

        form.resetFields();
        refreshCalendarData();
        onClose();
      } catch (error) {
        showNotification(error);
      }
    },
    [
      employeeId,
      isEmployeeSelectable,
      params.ActiveBranchId,
      params.UserId,
      updateWorkSchedule,
      showNotification,
      selectedDateMoment,
      addDisplayedEmployeeId,
      addActiveWorkdayEmployeeId,
      form,
      refreshCalendarData,
      onClose,
    ]
  );

  const handleCancel = useCallback(() => {
    form.resetFields();
    onClose();
  }, [form, onClose]);

  const timeSelectOptions = useMemo(() => timeOptions, []);

  const isSubmitDisabled = isEmployeeSelectable && !selectedEmployeeId;

  return {
    form,
    isLoading,
    employeeOptions,
    isEmployeeSelectable,
    isSubmitDisabled,
    selectedEmployeeName,
    timeSelectOptions,
    handleDateChange,
    handleEmployeeChange,
    handleFinish,
    handleCancel,
  };
};
